import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 * @property {HTMLElement} [gridContainer] - The grid container element.
 * @property {HTMLElement[]} [gridDots] - The individual grid dot elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Setup grid dots if in grid mode
    if (this.presentation === 'grid') {
      this.#setupGridDots();
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Sets up grid dots functionality
   */
  #setupGridDots() {
    const gridContainer = this.refs.gridContainer;
    const dotsContainer = this.refs.gridDots;
    
    if (!gridContainer || !dotsContainer) return;

    // Check if grid has vertical scrolling
    this.#updateDotsVisibility();
    
    // Setup scroll listener
    const handleScroll = () => {
      this.#updateActiveDot();
    };

    gridContainer.addEventListener('scroll', handleScroll, { 
      signal: this.#controller.signal,
      passive: true 
    });

    // Setup resize listener
    const handleResize = () => {
      this.#updateDotsVisibility();
    };

    window.addEventListener('resize', handleResize, { 
      signal: this.#controller.signal,
      passive: true 
    });
  }

  /**
   * Updates dots visibility based on grid scrollability
   */
  #updateDotsVisibility() {
    const gridContainer = this.refs.gridContainer;
    const dotsContainer = this.querySelector('.media-gallery__grid-dots');
    
    if (!gridContainer || !dotsContainer) {
      console.log('Grid container or dots container not found:', { gridContainer, dotsContainer });
      return;
    }

    const hasVerticalScroll = gridContainer.scrollHeight > gridContainer.clientHeight;
    
    // Show dots only on desktop when there's vertical scrolling
    if (hasVerticalScroll && window.innerWidth >= 750) {
      if (dotsContainer instanceof HTMLElement) {
        dotsContainer.style.display = 'block';
      }
      this.#updateActiveDot();
    } else {
      if (dotsContainer instanceof HTMLElement) {
        dotsContainer.style.display = 'none';
      }
    }
  }

  /**
   * Updates the active dot based on scroll position
   */
  #updateActiveDot() {
    const gridContainer = this.refs.gridContainer;
    const dots = this.querySelectorAll('.media-gallery__grid-dot');
    
    if (!gridContainer || !dots || dots.length === 0) return;

    const scrollTop = gridContainer.scrollTop;
    const scrollHeight = gridContainer.scrollHeight;
    const clientHeight = gridContainer.clientHeight;
    
    // Calculate which section is currently visible
    const scrollPercentage = scrollTop / (scrollHeight - clientHeight);
    const activeIndex = Math.round(scrollPercentage * (dots.length - 1));
    
    // Update active dot
    dots.forEach((dot, index) => {
      const isActive = index === activeIndex;
      dot.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      if (isActive) {
        dot.classList.add('active-dot');
      } else {
        dot.classList.remove('active-dot');
      }
    });
  }

  /**
   * Scrolls to a specific grid item
   * @param {number} index - The index of the item to scroll to
   */
  scrollToGridItem(index) {
    const gridContainer = this.refs.gridContainer;
    const mediaItems = this.refs.media;
    
    if (!gridContainer || !mediaItems || !mediaItems[index]) return;

    const targetItem = mediaItems[index];
    const containerRect = gridContainer.getBoundingClientRect();
    const itemRect = targetItem.getBoundingClientRect();
    
    const scrollTop = gridContainer.scrollTop + (itemRect.top - containerRect.top);
    
    gridContainer.scrollTo({
      top: scrollTop,
      behavior: 'smooth'
    });
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
